import {
  isWorldCupCompetition,
  resolveWorldCupTeamGroup,
  WORLD_CUP_ALL_GROUPS,
  type WorldCupGroup,
} from './worldCupTeamFilter';
import {
  FIFA_ROUND_32_TEMPLATE,
  getWorldCupThirdPlaceAssignment,
  type WorldCupRound32Slot,
} from './worldCupFifaBracketRules';

export const WORLD_CUP_GROUP_STAGE_SERIES_PREFIX = 'GROUP_STAGE-';
export const WORLD_CUP_GROUP_STAGE_MAX_QUALIFIERS = 32;
export const WORLD_CUP_GROUP_STAGE_MAX_QUALIFIERS_PER_GROUP = 3;
export const WORLD_CUP_GROUP_STAGE_AUTO_QUALIFIERS_PER_GROUP = 2;
export const WORLD_CUP_GROUP_STAGE_BEST_THIRD_QUALIFIERS = 8;

export type WorldCupGroupStageQualifierRole = 'AUTO' | 'BEST_THIRD';

export type WorldCupBracketTeam = {
  teamCode: string;
  teamName: string;
  shortName: string;
  seed: number;
  teamPrimaryColor?: string | null;
  teamSecondaryColor?: string | null;
  gamesWon?: number;
  gamesLost?: number;
};

export type WorldCupMatchup = {
  seriesCode: string;
  seriesName: string;
  higherSeed: WorldCupBracketTeam | null;
  lowerSeed: WorldCupBracketTeam | null;
  higherSeedSourceSeriesCode?: string | null;
  lowerSeedSourceSeriesCode?: string | null;
  higherSeedSourceOutcome?: 'WINNER' | 'LOSER' | null;
  lowerSeedSourceOutcome?: 'WINNER' | 'LOSER' | null;
  higherSeedPlaceholderLabel?: string | null;
  lowerSeedPlaceholderLabel?: string | null;
  status: 'PENDING' | 'ACTIVE' | 'COMPLETE';
  winnerTeamCode: string | null;
};

export type WorldCupRound = {
  roundCode: string;
  roundName: string;
  roundSequence: number;
  winsRequired: number;
  matchups: WorldCupMatchup[];
};

export type WorldCupBracket = {
  competitionCode: string;
  seasonCode: string;
  rounds: WorldCupRound[];
};

export type WorldCupMatchupPrediction = {
  winnerTeamCode: string;
  gamesWon: number;
  bestThirdRank?: number;
};

export type WorldCupGroupStageTeamPick<TTeam extends WorldCupBracketTeam = WorldCupBracketTeam> = {
  seriesCode: string;
  team: TTeam;
};

export type WorldCupGroupStageQualification<
  TTeam extends WorldCupBracketTeam = WorldCupBracketTeam,
> = WorldCupGroupStageTeamPick<TTeam> & {
  group: WorldCupGroup;
  groupRank: 1 | 2 | 3;
  role: WorldCupGroupStageQualifierRole;
  bestThirdRank?: number;
};

export type WorldCupGroupStageQualificationSummary = {
  autoQualifierCount: number;
  bestThirdCount: number;
  selectedCount: number;
  isComplete: boolean;
};

export type WorldCupGroupStagePanel<TTeam extends WorldCupBracketTeam = WorldCupBracketTeam> = {
  group: WorldCupGroup;
  title: string;
  teams: WorldCupGroupStageTeamPick<TTeam>[];
};

export function isWorldCupGroupStageSeriesCode(seriesCode: string | null | undefined): boolean {
  return (seriesCode ?? '').startsWith(WORLD_CUP_GROUP_STAGE_SERIES_PREFIX);
}

export function buildWorldCupGroupStagePanels<TRound extends WorldCupRound>(
  round: TRound | null | undefined,
): WorldCupGroupStagePanel<NonNullable<TRound['matchups'][number]['higherSeed']>>[] {
  if (!round || round.roundCode !== 'GROUP_STAGE') {
    return [];
  }

  type Team = NonNullable<TRound['matchups'][number]['higherSeed']>;
  const byGroup = new Map<WorldCupGroup, WorldCupGroupStageTeamPick<Team>[]>();
  round.matchups.forEach((matchup) => {
    const team = matchup.higherSeed as Team | null;
    if (!team || !isWorldCupGroupStageSeriesCode(matchup.seriesCode)) {
      return;
    }

    const group = resolveWorldCupTeamGroup({
      team_code: team.teamCode,
      group: null,
    });
    if (!group) {
      return;
    }

    const teams = byGroup.get(group) ?? [];
    teams.push({ seriesCode: matchup.seriesCode, team });
    byGroup.set(group, teams);
  });

  return WORLD_CUP_ALL_GROUPS
    .map((group) => {
      const teams = byGroup.get(group) ?? [];
      return {
        group,
        title: `Group ${group}`,
        teams: teams.sort((a, b) => a.team.seed - b.team.seed),
      };
    })
    .filter((panel) => panel.teams.length > 0);
}

export function countWorldCupGroupStageSelections(
  predictions: Map<string, WorldCupMatchupPrediction>,
): number {
  let count = 0;
  predictions.forEach((_prediction, seriesCode) => {
    if (isWorldCupGroupStageSeriesCode(seriesCode)) {
      count += 1;
    }
  });
  return count;
}

export function isWorldCupGroupStageLocked(round: WorldCupRound | null | undefined): boolean {
  if (!round || round.roundCode !== 'GROUP_STAGE') {
    return false;
  }

  const groupStageMatchups = round.matchups.filter((matchup) =>
    isWorldCupGroupStageSeriesCode(matchup.seriesCode)
  );
  return (
    groupStageMatchups.length > 0 &&
    groupStageMatchups.every((matchup) => matchup.status === 'COMPLETE')
  );
}

function getPredictionOrder(
  predictions: Map<string, WorldCupMatchupPrediction>,
): Map<string, number> {
  const order = new Map<string, number>();
  let index = 0;
  predictions.forEach((_prediction, seriesCode) => {
    order.set(seriesCode, index);
    index += 1;
  });
  return order;
}

export function buildWorldCupGroupStageQualifications<
  TRound extends WorldCupRound,
>(
  round: TRound | null | undefined,
  predictions: Map<string, WorldCupMatchupPrediction>,
): WorldCupGroupStageQualification<NonNullable<TRound['matchups'][number]['higherSeed']>>[] {
  const predictionOrder = getPredictionOrder(predictions);
  return buildWorldCupGroupStagePanels(round).flatMap((panel) => {
    const selectedPicks = panel.teams
      .filter((pick) => predictions.get(pick.seriesCode)?.winnerTeamCode === pick.team.teamCode)
      .sort((a, b) => {
        const aOrder = predictionOrder.get(a.seriesCode) ?? Number.MAX_SAFE_INTEGER;
        const bOrder = predictionOrder.get(b.seriesCode) ?? Number.MAX_SAFE_INTEGER;
        return (
          aOrder - bOrder ||
          a.team.seed - b.team.seed ||
          a.team.teamCode.localeCompare(b.team.teamCode)
        );
      });

    return selectedPicks.map((pick, index) => {
      const role =
        index < WORLD_CUP_GROUP_STAGE_AUTO_QUALIFIERS_PER_GROUP
          ? 'AUTO'
          : 'BEST_THIRD';
      const prediction = predictions.get(pick.seriesCode);
      const bestThirdRank =
        role === 'BEST_THIRD' && Number.isFinite(prediction?.bestThirdRank)
          ? Math.max(1, Math.trunc(prediction?.bestThirdRank ?? 0))
          : undefined;

      return {
        ...pick,
        group: panel.group,
        groupRank: (index + 1) as 1 | 2 | 3,
        role,
        bestThirdRank,
      };
    });
  });
}

export function buildWorldCupBestThirdOrder<TRound extends WorldCupRound>(
  round: TRound | null | undefined,
  predictions: Map<string, WorldCupMatchupPrediction>,
): WorldCupGroupStageQualification<NonNullable<TRound['matchups'][number]['higherSeed']>>[] {
  return buildWorldCupGroupStageQualifications(round, predictions)
    .filter((qualification) => qualification.role === 'BEST_THIRD')
    .sort((a, b) => {
      const aRank = a.bestThirdRank ?? Number.MAX_SAFE_INTEGER;
      const bRank = b.bestThirdRank ?? Number.MAX_SAFE_INTEGER;
      return (
        aRank - bRank ||
        a.group.localeCompare(b.group) ||
        a.team.seed - b.team.seed ||
        a.team.teamCode.localeCompare(b.team.teamCode)
      );
    });
}

export function normalizeWorldCupBestThirdRanks<TRound extends WorldCupRound>(
  round: TRound | null | undefined,
  predictions: Map<string, WorldCupMatchupPrediction>,
): Map<string, WorldCupMatchupPrediction> {
  const normalized = new Map<string, WorldCupMatchupPrediction>();
  predictions.forEach((prediction, seriesCode) => {
    const { bestThirdRank: _bestThirdRank, ...rest } = prediction;
    normalized.set(seriesCode, rest);
  });

  buildWorldCupBestThirdOrder(round, predictions).forEach((qualification, index) => {
    const prediction = normalized.get(qualification.seriesCode);
    if (!prediction) return;
    normalized.set(qualification.seriesCode, {
      ...prediction,
      bestThirdRank: index + 1,
    });
  });

  return normalized;
}

export function getWorldCupGroupStageQualificationSummary(
  round: WorldCupRound | null | undefined,
  predictions: Map<string, WorldCupMatchupPrediction>,
): WorldCupGroupStageQualificationSummary {
  const qualifications = buildWorldCupGroupStageQualifications(round, predictions);
  const autoQualifierCount = qualifications.filter(
    (qualification) => qualification.role === 'AUTO',
  ).length;
  const bestThirdCount = qualifications.filter(
    (qualification) => qualification.role === 'BEST_THIRD',
  ).length;
  const isComplete =
    autoQualifierCount ===
      WORLD_CUP_ALL_GROUPS.length * WORLD_CUP_GROUP_STAGE_AUTO_QUALIFIERS_PER_GROUP &&
    bestThirdCount === WORLD_CUP_GROUP_STAGE_BEST_THIRD_QUALIFIERS &&
    qualifications.length === WORLD_CUP_GROUP_STAGE_MAX_QUALIFIERS;

  return {
    autoQualifierCount,
    bestThirdCount,
    selectedCount: qualifications.length,
    isComplete,
  };
}

export function resolveWorldCupBestThirdTrayCollapsed(
  summary: WorldCupGroupStageQualificationSummary | null | undefined,
  localPreference?: boolean | null,
): boolean {
  if (typeof localPreference === 'boolean') {
    return localPreference;
  }
  return Boolean(summary?.isComplete);
}

export function getWorldCupGroupStageSelectedTeams(
  round: WorldCupRound | null | undefined,
  predictions: Map<string, WorldCupMatchupPrediction>,
): WorldCupBracketTeam[] {
  return buildWorldCupGroupStageQualifications(round, predictions)
    .sort((a, b) => {
      const aRolePriority = a.role === 'AUTO' ? 0 : 1;
      const bRolePriority = b.role === 'AUTO' ? 0 : 1;
      return (
        aRolePriority - bRolePriority ||
        a.team.seed - b.team.seed ||
        a.team.teamCode.localeCompare(b.team.teamCode)
      );
    })
    .map((qualification) => qualification.team);
}

function simulatedMatchup(
  seriesCode: string,
  seriesName: string,
  higherSeed: WorldCupBracketTeam | null,
  lowerSeed: WorldCupBracketTeam | null,
  source?: {
    higher: string;
    lower: string;
    outcome: 'WINNER' | 'LOSER';
  },
): WorldCupMatchup {
  return {
    seriesCode,
    seriesName,
    higherSeed,
    lowerSeed,
    higherSeedSourceSeriesCode: source?.higher ?? null,
    lowerSeedSourceSeriesCode: source?.lower ?? null,
    higherSeedSourceOutcome: source?.outcome ?? null,
    lowerSeedSourceOutcome: source?.outcome ?? null,
    higherSeedPlaceholderLabel: higherSeed ? null : 'Pick previous round winner',
    lowerSeedPlaceholderLabel: lowerSeed ? null : 'Pick previous round winner',
    status: higherSeed && lowerSeed ? 'ACTIVE' : 'PENDING',
    winnerTeamCode: null,
  };
}

function buildFixedSourceRound(
  roundCode: string,
  roundName: string,
  roundSequence: number,
  sourcePairs: readonly {
    seriesCode: string;
    higher: string;
    lower: string;
    outcome?: 'WINNER' | 'LOSER';
  }[],
): WorldCupRound {
  return {
    roundCode,
    roundName,
    roundSequence,
    winsRequired: 1,
    matchups: sourcePairs.map((sourcePair) =>
      simulatedMatchup(
        sourcePair.seriesCode,
        `Match ${sourcePair.seriesCode.slice(1)}`,
        null,
        null,
        {
          higher: sourcePair.higher,
          lower: sourcePair.lower,
          outcome: sourcePair.outcome ?? 'WINNER',
        },
      ),
    ),
  };
}

function slotLabelForQualification(qualification: WorldCupGroupStageQualification): `${WorldCupGroup}${1 | 2 | 3}` {
  return `${qualification.group}${qualification.groupRank}`;
}

function resolveFifaRound32Slot(
  slot: WorldCupRound32Slot,
  teamsBySlot: Map<string, WorldCupBracketTeam>,
  thirdPlaceAssignment: ReturnType<typeof getWorldCupThirdPlaceAssignment>,
  seriesCode: string,
): { team: WorldCupBracketTeam; label: string } | null {
  if (!slot.startsWith('3rd ')) {
    const team = teamsBySlot.get(slot);
    return team ? { team, label: slot } : null;
  }

  const thirdPlaceGroup = thirdPlaceAssignment?.[seriesCode as keyof NonNullable<typeof thirdPlaceAssignment>];
  if (!thirdPlaceGroup) {
    return null;
  }

  const label = `${thirdPlaceGroup}3`;
  const team = teamsBySlot.get(label);
  return team ? { team, label } : null;
}

export function buildWorldCupSimulatedKnockoutRounds(
  groupStageRound: WorldCupRound | null | undefined,
  predictions: Map<string, WorldCupMatchupPrediction>,
): WorldCupRound[] {
  const summary = getWorldCupGroupStageQualificationSummary(groupStageRound, predictions);
  if (!summary.isComplete) {
    return [];
  }

  const qualifications = buildWorldCupGroupStageQualifications(groupStageRound, predictions);
  const teamsBySlot = new Map(
    qualifications.map((qualification) => [
      slotLabelForQualification(qualification),
      qualification.team,
    ]),
  );
  const thirdPlaceGroups = qualifications
    .filter((qualification) => qualification.role === 'BEST_THIRD')
    .map((qualification) => qualification.group);
  const thirdPlaceAssignment = getWorldCupThirdPlaceAssignment(thirdPlaceGroups);
  if (!thirdPlaceAssignment) {
    return [];
  }

  const baseSequence = groupStageRound?.roundSequence ?? 1;
  const round32Matchups: WorldCupMatchup[] = [];
  for (const template of FIFA_ROUND_32_TEMPLATE) {
    const matchupNumber = Number(template.seriesCode.slice(1));
    const higherSeed = resolveFifaRound32Slot(
      template.higherSlot,
      teamsBySlot,
      thirdPlaceAssignment,
      template.seriesCode,
    );
    const lowerSeed = resolveFifaRound32Slot(
      template.lowerSlot,
      teamsBySlot,
      thirdPlaceAssignment,
      template.seriesCode,
    );
    if (!higherSeed || !lowerSeed) {
      return [];
    }
    round32Matchups.push({
      ...simulatedMatchup(
        template.seriesCode,
        `Match ${matchupNumber} - ${higherSeed.label} vs ${lowerSeed.label}`,
        { ...higherSeed.team, gamesWon: 0, gamesLost: 0 },
        { ...lowerSeed.team, gamesWon: 0, gamesLost: 0 },
      ),
      higherSeedPlaceholderLabel: higherSeed.label,
      lowerSeedPlaceholderLabel: lowerSeed.label,
    });
  }

  const round32: WorldCupRound = {
    roundCode: 'ROUND_32',
    roundName: 'Round of 32',
    roundSequence: baseSequence + 1,
    winsRequired: 1,
    matchups: round32Matchups,
  };
  const round16 = buildFixedSourceRound(
    'ROUND_16',
    'Round of 16',
    baseSequence + 2,
    [
      { seriesCode: 'M89', higher: 'M74', lower: 'M77' },
      { seriesCode: 'M90', higher: 'M73', lower: 'M75' },
      { seriesCode: 'M91', higher: 'M76', lower: 'M78' },
      { seriesCode: 'M92', higher: 'M79', lower: 'M80' },
      { seriesCode: 'M93', higher: 'M83', lower: 'M84' },
      { seriesCode: 'M94', higher: 'M81', lower: 'M82' },
      { seriesCode: 'M95', higher: 'M86', lower: 'M88' },
      { seriesCode: 'M96', higher: 'M85', lower: 'M87' },
    ],
  );
  const quarterFinal = buildFixedSourceRound(
    'QUARTER_FINAL',
    'Quarter Finals',
    baseSequence + 3,
    [
      { seriesCode: 'M97', higher: 'M89', lower: 'M90' },
      { seriesCode: 'M98', higher: 'M93', lower: 'M94' },
      { seriesCode: 'M99', higher: 'M91', lower: 'M92' },
      { seriesCode: 'M100', higher: 'M95', lower: 'M96' },
    ],
  );
  const semiFinal = buildFixedSourceRound(
    'SEMI_FINAL',
    'Semi-Finals',
    baseSequence + 4,
    [
      { seriesCode: 'M101', higher: 'M97', lower: 'M98' },
      { seriesCode: 'M102', higher: 'M99', lower: 'M100' },
    ],
  );
  const thirdPlace = buildFixedSourceRound(
    'THIRD_PLACE',
    'Match for 3rd place',
    baseSequence + 5,
    [{ seriesCode: 'M103', higher: 'M101', lower: 'M102', outcome: 'LOSER' }],
  );
  const final = buildFixedSourceRound(
    'FINAL',
    'Final',
    baseSequence + 6,
    [{ seriesCode: 'M104', higher: 'M101', lower: 'M102' }],
  );

  return [round32, round16, quarterFinal, semiFinal, thirdPlace, final];
}

function hasOfficialRound32Participants(bracket: WorldCupBracket): boolean {
  const round32 = bracket.rounds.find((round) => round.roundCode === 'ROUND_32');
  return !!round32?.matchups.some((matchup) => matchup.higherSeed || matchup.lowerSeed);
}

export function buildWorldCupBracketWithSimulatedKnockout<TBracket extends WorldCupBracket>(
  bracket: TBracket | null,
  predictions: Map<string, WorldCupMatchupPrediction>,
  competitionCode: string | null | undefined,
): TBracket | null {
  if (!bracket || !isWorldCupCompetition(competitionCode)) {
    return bracket;
  }
  if (hasOfficialRound32Participants(bracket)) {
    return bracket;
  }

  const groupStageRound = bracket.rounds.find((round) => round.roundCode === 'GROUP_STAGE');
  const simulatedRounds = buildWorldCupSimulatedKnockoutRounds(groupStageRound, predictions);
  if (!simulatedRounds.length) {
    return bracket;
  }

  const simulatedRoundCodes = new Set(simulatedRounds.map((round) => round.roundCode));
  return {
    ...bracket,
    rounds: [
      ...bracket.rounds.filter((round) => !simulatedRoundCodes.has(round.roundCode)),
      ...simulatedRounds,
    ].sort((a, b) => a.roundSequence - b.roundSequence),
  };
}
